import { useState } from 'react';

import type { BiomeKind } from '../generated/BiomeKind';
import type { ServerVisibility } from '../generated/ServerVisibility';
import type { ChunkCoordJson } from '../platform/api';
import { useEmbark } from '../platform/store';
import { useToasts } from '../toasts';
import styles from './embark.module.css';

export function ClaimPanel({
  chunk,
  biome,
  onCancel,
  onCreated,
}: {
  chunk: ChunkCoordJson;
  biome: BiomeKind | null;
  onCancel: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<ServerVisibility>('Public');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const doCreateClaim = useEmbark((s) => s.doCreateClaim);

  const validate = (v: string): string => {
    if (v.length === 0) return 'Name is required';
    if (v.length > 64) return 'Name must be 64 characters or fewer';
    if (!/^[a-zA-Z0-9 -]+$/.test(v))
      return 'Only letters, numbers, spaces, and hyphens allowed';
    return '';
  };

  const handleNameChange = (v: string) => {
    setName(v);
    setError(validate(v));
  };

  const handleSubmit = async () => {
    const err = validate(name);
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    const claim = await doCreateClaim(name, visibility);
    setSubmitting(false);
    if (claim) {
      onCreated();
      useToasts
        .getState()
        .push('info', 'Server not yet running. Coming soon!', 4000);
    }
  };

  return (
    <div className={styles.panelOverlay} onClick={onCancel}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.panelTitle}>Create World</div>

        <div className={styles.panelField}>
          <span className={styles.panelLabel}>Chunk</span>
          <span className={styles.panelValue}>
            ({chunk.x}, {chunk.y})
          </span>
        </div>

        {biome && (
          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Biome</span>
            <span className={styles.panelValue}>{biome}</span>
          </div>
        )}

        <div className={styles.panelField}>
          <label className={styles.panelLabel} htmlFor="claim-name">
            Name your world
          </label>
          <input
            id="claim-name"
            className={styles.panelInput}
            type="text"
            maxLength={64}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="My World"
            autoFocus
          />
          {error && <div className={styles.panelError}>{error}</div>}
        </div>

        <div className={styles.panelField}>
          <span className={styles.panelLabel}>Visibility</span>
          <div className={styles.radioGroup}>
            {(['Public', 'FriendsOnly', 'Private'] as ServerVisibility[]).map(
              (v) => (
                <label key={v} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="visibility"
                    value={v}
                    checked={visibility === v}
                    onChange={() => setVisibility(v)}
                  />
                  {v === 'FriendsOnly' ? 'Friends Only' : v}
                </label>
              ),
            )}
          </div>
          <div style={{ fontSize: 11, color: '#949ba4', marginTop: 4 }}>
            Controls who can join as a player. SOMAs always travel freely.
          </div>
        </div>

        <div className={styles.panelActions}>
          <button
            className={styles.primaryBtn}
            disabled={submitting || !!error || name.length === 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Creating...' : 'Create World'}
          </button>
          <button className={styles.secondaryBtn} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
